import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, FONTS } from '../../tokens/design';
import { useClientProfile, useClientAlerts } from '../../hooks/useClientData';
import { relativeTime, exactTime } from '../../utils/time';
import Shell from '../../components/layout/Shell';
import Card from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import PageError from '../../components/ui/PageError';
import Skeleton from '../../components/ui/Skeleton';
import SeverityBadge from '../../components/badges/SeverityBadge';
import Tag from '../../components/ui/Tag';
import { countAlertsByFilter, demoVisibleAlerts, groupAlerts } from '../../utils/mspPolish';

export default function AlertsPage({ isMSP = false }) {
  const navigate = useNavigate();
  const { data: CLIENT } = useClientProfile();
  const { data: ALERTS, loading, error } = useClientAlerts();
  const [filter, setFilter] = useState('all');
  const [readIds, setReadIds] = useState(new Set());

  useEffect(() => {
    if (ALERTS.length && readIds.size === 0) {
      setReadIds(new Set(ALERTS.filter(a => a.read).map(a => a.id)));
    }
  }, [ALERTS]);

  const all = isMSP ? demoVisibleAlerts(ALERTS) : ALERTS.filter(a => a.client === CLIENT?.name);
  const visibleAlerts = isMSP ? groupAlerts(all) : all;
  const counts = isMSP ? countAlertsByFilter(all) : {
    all: all.length,
    unread: all.filter(a => !readIds.has(a.id)).length,
    critical: all.filter(a => a.severity === 'critical').length,
    high: all.filter(a => a.severity === 'high').length,
    medium: all.filter(a => a.severity === 'medium').length,
  };
  const filtered = filter === 'all'
    ? visibleAlerts
    : filter === 'unread'
      ? visibleAlerts.filter(a => !readIds.has(a.id))
      : visibleAlerts.filter(a => a.severity === filter);

  const markRead = (id) => setReadIds(prev => new Set([...prev, id]));
  const markAllRead = () => setReadIds(new Set(all.map(a => a.id)));

  const unreadLabel = isMSP ? counts.unread : all.filter(a => !readIds.has(a.id)).length;

  return (
    <Shell title="Alerts" sub={loading ? 'Loading...' : `${unreadLabel} unread`}>
      {error && <PageError error={`Failed to load alerts: ${error}`} onRetry={() => window.location.reload()} />}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {['all', 'unread', 'critical', 'high', 'medium'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '5px 12px', borderRadius: 6,
            border: `1px solid ${filter === f ? C.b1 : 'transparent'}`,
            cursor: 'pointer', background: filter === f ? C.s2 : 'transparent',
            color: filter === f ? C.t1 : C.t2, fontSize: 11,
            fontWeight: filter === f ? 600 : 400, fontFamily: FONTS.sans,
          }}>{f.charAt(0).toUpperCase() + f.slice(1)} <span style={{ color: filter === f ? C.sky : C.t3 }}>({counts[f] ?? 0})</span></button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={markAllRead} style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.b1}`, background: C.s2, color: C.t2, fontSize: 10, fontFamily: FONTS.mono, cursor: 'pointer' }}>Mark all read</button>
      </div>

      <Card>
        {loading && !error && [1, 2, 3, 4, 5].map(i => (
          <div key={i} style={{ padding: '13px 16px', borderBottom: `1px solid ${C.b2}`, display: 'flex', gap: 10, alignItems: 'center' }}>
            <Skeleton width={70} height={22} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Skeleton width="60%" height={12} />
              <Skeleton width="30%" height={9} />
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && <EmptyState title="No alerts match your filter" sub="All caught up!" />}
        {!loading && filtered.map(a => {
          const isUnread = !readIds.has(a.id);
          return (
            <div key={a.id} onClick={() => markRead(a.id)} style={{
              padding: '13px 16px', borderBottom: `1px solid ${C.b2}`,
              display: 'flex', gap: 10, alignItems: 'flex-start',
              background: isUnread ? `${C.redBg}66` : 'transparent', cursor: 'pointer',
            }} onMouseEnter={e => e.currentTarget.style.background = C.s2}
               onMouseLeave={e => e.currentTarget.style.background = isUnread ? `${C.redBg}66` : 'transparent'}>
              <SeverityBadge value={a.severity || 'info'} />
              <div style={{ flex: 1 }}>
                {isMSP && <div style={{ fontSize: 9, color: C.sky, fontFamily: FONTS.mono, marginBottom: 3 }}>{a.client}</div>}
                <div style={{ fontSize: 12, color: isUnread ? C.t1 : C.t2, fontWeight: isUnread ? 500 : 400, lineHeight: 1.4, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span>{a.msg}</span>
                  {isMSP && a.count > 1 && <Tag label={`x${a.count}`} color={C.red} />}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                  <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }} title={exactTime(a.time)}>{relativeTime(a.time)}</span>
                  {a.findingId
                    ? <span onClick={(e) => { e.stopPropagation(); navigate(`/client/findings/${a.findingId}`); }} style={{ fontSize: 9, color: C.sky, fontFamily: FONTS.mono, cursor: 'pointer' }}>View Finding {'\u2192'}</span>
                    : <span onClick={(e) => { e.stopPropagation(); navigate('/client/osint'); }} style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, cursor: 'pointer' }}>View OSINT {'\u2192'}</span>
                  }
                </div>
              </div>
              {isUnread && <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.red, marginTop: 4, flexShrink: 0 }} />}
            </div>
          );
        })}
      </Card>
    </Shell>
  );
}
